if (!customElements.get('product-specs-modal')) {
  customElements.define(
    'product-specs-modal',
    class ProductSpecsModal extends ModalWindow {
      constructor() {
        super();
        this.slideDurationMilliseconds = 300;
        this.announcementDurationMilliseconds = 3000;
        this.swipeThresholdPixels = 40;
      }

      connectedCallback() {
        super.connectedCallback();
        this.initSiblingSwitcher();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.handleSiblingSwitchClickBound && this.siblingSwitchButton) {
          this.siblingSwitchButton.removeEventListener('click', this.handleSiblingSwitchClickBound);
        }
        if (this.handleSwitcherRealignBound) {
          window.removeEventListener('resize', this.handleSwitcherRealignBound);
        }
        if (this.specsView) {
          if (this.handleSpecsPointerDownBound) {
            this.specsView.removeEventListener('pointerdown', this.handleSpecsPointerDownBound);
          }
          if (this.handleSpecsPointerUpBound) {
            this.specsView.removeEventListener('pointerup', this.handleSpecsPointerUpBound);
            this.specsView.removeEventListener('pointercancel', this.handleSpecsPointerUpBound);
          }
        }
        if (this.handleDotKeydownBound && this.siblingDotsContainer) {
          this.siblingDotsContainer.removeEventListener('keydown', this.handleDotKeydownBound);
        }
        // Tear down any in-flight slide so a detach mid-slide can't later fire the
        // fallback timer / transitionend listener → clearSlideState on a detached
        // node. clearSlideState is idempotent and safe when no slide is active.
        this.clearSlideState();
        this.clearAnnouncementTimeout();
      }

      /**
       * Wire up the combined-listing sibling switcher: a single forward-cycling
       * button (bottom-right of the spec card) that flips the card to the next
       * sibling's specs. Client-side only — the page's product/variant selection,
       * buy bar, gallery and URL are untouched. The button is server-rendered
       * only when at least one sibling resolves, so a missing button means
       * "nothing to wire".
       */
      initSiblingSwitcher() {
        this.specsView = this.querySelector('[data-specs-view]');
        this.siblingSwitchButton = this.querySelector('[data-specs-sibling-switch]');
        this.siblingAnnouncementElement = this.querySelector('[data-specs-sibling-announcement]');

        if (!this.specsView || !this.siblingSwitchButton) return;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.productScope = this.closest('product-controller') || document;
        this.siblingProductStates = this.readSiblingProductStates();

        // Cycling needs at least two cards (the page's product + one sibling).
        // Server gating should already guarantee this; if sibling JSON parsed
        // down to fewer states, remove the dead control entirely.
        if (this.siblingProductStates.length < 2) {
          const footerElement = this.siblingSwitchButton.closest('.product-specs-modal-footer');
          if (footerElement) {
            footerElement.remove();
          } else {
            this.siblingSwitchButton.remove();
          }
          this.siblingSwitchButton = null;
          return;
        }

        /** Product id of the card currently shown in the modal. */
        this.displayedProductId = null;
        /** True once the switcher has written a card that may differ from page state. */
        this.cardSwapped = false;
        /** True while a card slide animation is in flight (re-entrancy guard). */
        this.isSliding = false;
        this.announcementTimeout = null;

        this.handleSiblingSwitchClickBound = this.handleSiblingSwitchClick.bind(this);
        this.siblingSwitchButton.addEventListener('click', this.handleSiblingSwitchClickBound);

        // The switcher is pinned to the initial card's center and held there for
        // all siblings (see lockSwitcherPosition), so it doesn't jump when a
        // sibling's spec column wraps to a different height. Resize is the only
        // time the initial card's height changes, so re-measure then.
        this.handleSwitcherRealignBound = () => requestAnimationFrame(() => this.lockSwitcherPosition());
        window.addEventListener('resize', this.handleSwitcherRealignBound);

        // Pagination dots (position indicator + tap targets) and swipe both drive
        // the same swap as the arrow. Dots are built from the sibling states;
        // swipe uses Pointer Events on the card body so it works for mouse drag
        // (desktop), touch, and pen. `touch-action: pan-y` (CSS) keeps vertical
        // scrolling with the browser while reserving horizontal for the swipe.
        this.siblingDotsContainer = this.querySelector('[data-specs-sibling-dots]');
        this.buildSiblingDots();

        this.handleSpecsPointerDownBound = this.handleSpecsPointerDown.bind(this);
        this.handleSpecsPointerUpBound = this.handleSpecsPointerUp.bind(this);
        this.specsView.addEventListener('pointerdown', this.handleSpecsPointerDownBound);
        this.specsView.addEventListener('pointerup', this.handleSpecsPointerUpBound);
        this.specsView.addEventListener('pointercancel', this.handleSpecsPointerUpBound);
      }

      /**
       * Read every sibling's pre-rendered JSON state — the
       * `<script data-json-product-id>` elements `product-controller-default`
       * emits in `sibling_handles` order. Unparseable entries are skipped (they
       * drop out of the cycle); never throws.
       *
       * @returns {Array<Object>} Parsed product states in sibling order
       */
      readSiblingProductStates() {
        const productStateScripts = this.productScope.querySelectorAll('script[data-json-product-id]');
        const productStates = [];

        productStateScripts.forEach((productStateScript) => {
          try {
            const productState = JSON.parse(productStateScript.textContent);
            if (productState && productState.id != null) productStates.push(productState);
          } catch (error) {
            console.warn(
              'product-specs-modal: skipping unparseable sibling product state',
              productStateScript.getAttribute('data-json-product-id'),
              error
            );
          }
        });

        return productStates;
      }

      /**
       * The product state after `currentProductId` in the cycle, wrapping from
       * the last sibling back to the first (the page's product).
       *
       * @param {string|null} currentProductId
       * @returns {Object|null}
       */
      getNextProductState(currentProductId) {
        const currentIndex = this.siblingProductStates.findIndex(
          (productState) => String(productState.id) === String(currentProductId)
        );
        // Unknown current card — restart the cycle at the first state.
        if (currentIndex === -1) return this.siblingProductStates[0] || null;
        return this.siblingProductStates[(currentIndex + 1) % this.siblingProductStates.length];
      }

      /**
       * The product state before `currentProductId`, wrapping from the first
       * sibling around to the last — the mirror of getNextProductState, for a
       * swipe-right / previous move.
       *
       * @param {string|null} currentProductId
       * @returns {Object|null}
       */
      getPreviousProductState(currentProductId) {
        const count = this.siblingProductStates.length;
        if (count === 0) return null;
        const currentIndex = this.siblingProductStates.findIndex(
          (productState) => String(productState.id) === String(currentProductId)
        );
        if (currentIndex === -1) return this.siblingProductStates[0];
        return this.siblingProductStates[(currentIndex - 1 + count) % count];
      }

      /**
       * Index of `productId` within the ordered sibling states, or -1.
       *
       * @param {string|null} productId
       * @returns {number}
       */
      getProductStateIndex(productId) {
        return this.siblingProductStates.findIndex(
          (productState) => String(productState.id) === String(productId)
        );
      }

      /**
       * The right-edge arrow is an intentional forward-only cycle with
       * wrap-around (last sibling → first). Bidirectional + random-access
       * keyboard navigation lives on the pagination dots (`handleDotKeydown`:
       * Left/Right/Up/Down/Home/End) and on swipe — so a single forward arrow is
       * deliberate, not a missing "previous" affordance.
       */
      handleSiblingSwitchClick() {
        this.switchToProductState(this.getNextProductState(this.displayedProductId));
      }

      /**
       * Swap the card to `nextProductState`. Shared by all three affordances —
       * the right-edge arrow, the pagination dots, and swipe. Matches the
       * displayed Finish/Scent to a variant of the target, slides the card
       * content out and the new card in, and re-syncs the arrow label, the dots,
       * and the live region. No-op when the target is missing or already
       * displayed.
       *
       * @param {Object|null} nextProductState
       * @param {'forward'|'backward'} [direction='forward'] - Slide direction:
       *   forward exits the old card to the left and brings the new one in from
       *   the right; backward is the mirror. Drives `slideCardUpdate`.
       */
      switchToProductState(nextProductState, direction = 'forward') {
        if (!nextProductState) return;
        if (String(nextProductState.id) === String(this.displayedProductId)) return;

        const displayedFinishScentValue = this.readDisplayedFinishScentValue();
        const nextVariant = this.matchSiblingVariant(nextProductState, displayedFinishScentValue);

        this.slideCardUpdate(() => {
          this.renderSpecCard(nextProductState, nextVariant);
          this.displayedProductId = String(nextProductState.id);
          this.cardSwapped = true;
          this.updateSiblingSwitchButton();
          this.updateSiblingDots();
          this.announceCardChange(this.getProductStateDisplayName(nextProductState));
        }, direction);
      }

      /**
       * Build one dot button per sibling (in sibling order) into the dots
       * container, each switching to its sibling on click; the dot aria-labels
       * reuse the arrow's "Show specifications for {model}" template. No-op when
       * the container is absent (modal variants without the dots markup).
       *
       * The dots form a single roving-tabindex group (carousel-accessibility.md):
       * the group is one tab stop (only the active dot is tabbable), and arrow /
       * Home / End keys move between dots — `handleDotKeydown` is wired once on
       * the container and cleaned up in disconnectedCallback. The dot click
       * handlers are anonymous (not stored): no leak risk — the dots are created
       * here, only ever appended inside this component, and discarded via
       * `textContent = ''` (unlike the instance-level listeners the other
       * handlers store so they can be removed from shared targets).
       */
      buildSiblingDots() {
        if (!this.siblingDotsContainer) return;
        const labelTemplate = this.siblingSwitchButton
          ? this.siblingSwitchButton.getAttribute('data-switch-label-template') || ''
          : '';

        this.siblingDotsContainer.textContent = '';
        const totalSiblings = this.siblingProductStates.length;
        this.siblingDotButtons = this.siblingProductStates.map((productState, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-specs-sibling-dot';
          dot.setAttribute('data-specs-sibling-dot', '');
          // Set/position semantics so AT announces "n of total" for each dot.
          dot.setAttribute('aria-setsize', String(totalSiblings));
          dot.setAttribute('aria-posinset', String(index + 1));
          const modelName = this.getProductStateDisplayName(productState);
          dot.setAttribute('aria-label', labelTemplate ? labelTemplate.replace('{model}', modelName) : modelName);
          // Anonymous listener is intentional (not stored/removed like the
          // instance-level handlers): the dot rides with the element, which is
          // only appended inside this component and discarded via
          // `textContent = ''`, so there's nothing to leak.
          dot.addEventListener('click', () =>
            this.switchToProductState(
              productState,
              index > this.getProductStateIndex(this.displayedProductId) ? 'forward' : 'backward'
            )
          );
          this.siblingDotsContainer.appendChild(dot);
          return dot;
        });

        this.handleDotKeydownBound = this.handleDotKeydown.bind(this);
        this.siblingDotsContainer.addEventListener('keydown', this.handleDotKeydownBound);

        this.updateSiblingDots();
      }

      /**
       * Mark the dot for the displayed card active (`is-active` + aria-current)
       * and the single tabbable member of the roving group (`tabindex="0"`);
       * clear the rest (`aria-current="false"`, `tabindex="-1"`).
       */
      updateSiblingDots() {
        if (!this.siblingDotButtons) return;
        const currentIndex = this.getProductStateIndex(this.displayedProductId);
        this.siblingDotButtons.forEach((dot, index) => {
          const isActive = index === currentIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          dot.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      }

      /**
       * Roving-tabindex keyboard nav across the pagination dots: Left/Up →
       * previous, Right/Down → next, Home → first, End → last (clamped to the
       * ends). Switches the card to the target sibling AND moves focus to the
       * now-active dot, so keyboard users get bidirectional + random access (the
       * arrow is a forward-only convenience). No-op for other keys.
       *
       * @param {KeyboardEvent} event
       */
      handleDotKeydown(event) {
        if (!this.siblingDotButtons || this.siblingDotButtons.length === 0) return;

        // Drop the key while a slide is in flight: switchToProductState would
        // no-op via the isSliding guard (leaving displayedProductId, aria-current,
        // the roving tabindex and the live region untouched), but we'd still move
        // focus to the target dot — desyncing focus from the active dot for the
        // ~300ms window. Match how the arrow and swipe are already dropped mid-slide.
        if (this.isSliding) {
          event.preventDefault();
          return;
        }

        const lastIndex = this.siblingDotButtons.length - 1;
        const currentIndex = this.getProductStateIndex(this.displayedProductId);
        const fromIndex = currentIndex === -1 ? 0 : currentIndex;
        let targetIndex = null;

        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            targetIndex = Math.max(0, fromIndex - 1);
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            targetIndex = Math.min(lastIndex, fromIndex + 1);
            break;
          case 'Home':
            targetIndex = 0;
            break;
          case 'End':
            targetIndex = lastIndex;
            break;
          default:
            return;
        }

        event.preventDefault();
        if (targetIndex === fromIndex) {
          // Already at the edge — keep focus on the active dot.
          this.siblingDotButtons[targetIndex].focus();
          return;
        }

        this.switchToProductState(
          this.siblingProductStates[targetIndex],
          targetIndex > fromIndex ? 'forward' : 'backward'
        );
        // Focus the target dot. Native <button>s are focusable regardless of
        // tabindex, so this works immediately even while the cross-fade defers
        // updateSiblingDots (which then settles tabindex="0" onto this dot).
        this.siblingDotButtons[targetIndex].focus();
      }

      /**
       * Record the pointer-down point for swipe detection (mouse, touch, or pen).
       * Ignores secondary mouse buttons.
       *
       * @param {PointerEvent} event
       */
      handleSpecsPointerDown(event) {
        // Loose `!= null` is intentional: it treats both null and undefined
        // `button` (non-mouse pointers that omit it) as the primary press.
        if (event.button != null && event.button !== 0) return;
        this.swipeStartX = event.clientX;
        this.swipeStartY = event.clientY;
      }

      /**
       * On pointer up, advance to the next sibling on a leftward drag/swipe and
       * the previous on a rightward one. Ignores vertical-dominant moves (scroll),
       * drags shorter than the threshold, pointer cancels, and gestures that left
       * a text selection (so drag-selecting copy doesn't switch the card).
       *
       * @param {PointerEvent} event
       */
      handleSpecsPointerUp(event) {
        const startX = this.swipeStartX;
        const startY = this.swipeStartY;
        this.swipeStartX = null;
        this.swipeStartY = null;
        if (startX == null || event.type === 'pointercancel') return;

        const selection = window.getSelection && window.getSelection().toString();
        if (selection) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        // Horizontal intent only.
        if (Math.abs(deltaX) < this.swipeThresholdPixels || Math.abs(deltaX) <= Math.abs(deltaY)) return;

        if (deltaX < 0) {
          this.switchToProductState(this.getNextProductState(this.displayedProductId), 'forward');
        } else {
          this.switchToProductState(this.getPreviousProductState(this.displayedProductId), 'backward');
        }
      }

      /**
       * The Finish (or Scent) value currently visible on the card. Read from the
       * DOM so consecutive swaps always match against what the user sees.
       *
       * @returns {string} Trimmed value, or '' when the row is absent/hidden
       */
      readDisplayedFinishScentValue() {
        const finishScentValueElement = this.querySelector('[spec-variant-finish-scent]');
        if (!finishScentValueElement) return '';
        const finishScentRow = finishScentValueElement.closest('[data-spec]');
        if (finishScentRow && finishScentRow.hidden) return '';
        return finishScentValueElement.textContent.trim();
      }

      /**
       * Normalized row title (e.g. "Finish:") for a native spec row, read from
       * the DOM so it matches the localized titles in the variant `specs` JSON
       * (the same locale renders both).
       *
       * @param {string} valueSelector - Selector for the row's value element
       * @returns {string}
       */
      readSpecRowTitle(valueSelector) {
        const valueElement = this.querySelector(valueSelector);
        if (!valueElement) return '';
        const rowElement = valueElement.closest('[data-spec]');
        const titleElement = rowElement ? rowElement.querySelector('.spec__row-title') : null;
        return titleElement ? this.normalizeSpecTitle(titleElement.textContent) : '';
      }

      /**
       * @param {string} title
       * @returns {string} Lowercased, trimmed, trailing colon stripped
       */
      normalizeSpecTitle(title) {
        return (title || '').trim().toLowerCase().replace(/:$/, '');
      }

      /**
       * Pick the sibling variant whose Finish/Scent value matches the currently
       * displayed card; fall back to the sibling's first available variant, then
       * its first variant, when no match exists.
       *
       * @param {Object} productState - The sibling's parsed JSON state
       * @param {string} displayedFinishScentValue
       * @returns {Object|null}
       */
      matchSiblingVariant(productState, displayedFinishScentValue) {
        const variants = Array.isArray(productState.variants) ? productState.variants : [];
        if (variants.length === 0) return null;

        if (displayedFinishScentValue) {
          const finishScentTitle = this.readSpecRowTitle('[spec-variant-finish-scent]');
          if (finishScentTitle) {
            const matchedVariant = variants.find((variant) =>
              (Array.isArray(variant.specs) ? variant.specs : []).some(
                (spec) =>
                  this.normalizeSpecTitle(spec.title) === finishScentTitle &&
                  (spec.value || '').trim() === displayedFinishScentValue
              )
            );
            if (matchedVariant) return matchedVariant;
          }
        }

        // `available` is serialized as the string "true" / "false".
        const firstAvailableVariant = variants.find((variant) => variant.available === 'true');
        return firstAvailableVariant || variants[0];
      }

      /**
       * Value for a native row, matched by the row's localized DOM title against
       * the entries in a `specs` array (variant- or product-level).
       *
       * @param {Array<Object>|undefined} specs
       * @param {string} valueSelector
       * @returns {string}
       */
      findSpecValueByRowTitle(specs, valueSelector) {
        if (!Array.isArray(specs)) return '';
        const rowTitle = this.readSpecRowTitle(valueSelector);
        if (!rowTitle) return '';
        const matchedSpec = specs.find((spec) => this.normalizeSpecTitle(spec.title) === rowTitle);
        return matchedSpec ? (matchedSpec.value || '').trim() : '';
      }

      /**
       * Write a product's card into the modal. Mirrors the injection mechanism
       * `_DefaultProductMetaHandler` uses for page-driven updates: textContent
       * for native rows, DOMPurify-sanitized server-rendered HTML for the
       * metaobject spec groups, srcset/src swap for the dimension image.
       *
       * @param {Object} productState - Parsed sibling JSON state
       * @param {Object|null} variant - The variant entry to display
       */
      renderSpecCard(productState, variant) {
        const titleElement = this.querySelector('[data-specs-modal-title]');
        if (titleElement) titleElement.textContent = productState.title_native || '';

        const modelNameElement = this.querySelector('[data-specs-modal-model]');
        if (modelNameElement) modelNameElement.textContent = productState.model_name || '';

        this.updateDimensionImage(productState);

        this.setSpecGroupHtml(
          this.querySelector('[data-specs-product-custom-wrap]'),
          typeof productState.product_specs_html === 'string' ? productState.product_specs_html : ''
        );
        this.setSpecGroupHtml(
          this.querySelector('[data-specs-variant-custom-wrap]'),
          variant && typeof variant.variant_specs_html === 'string' ? variant.variant_specs_html : ''
        );

        const variantSpecs = variant ? variant.specs : null;
        this.updateNativeSpecRow(
          '[spec-variant-finish-scent]',
          this.findSpecValueByRowTitle(variantSpecs, '[spec-variant-finish-scent]')
        );
        // Warranty mirrors the server's variant-then-product fallback.
        const warrantyValue =
          this.findSpecValueByRowTitle(variantSpecs, '[spec-variant-warranty]') ||
          this.findSpecValueByRowTitle(productState.specs, '[spec-variant-warranty]');
        this.updateNativeSpecRow('[spec-variant-warranty]', warrantyValue);
        this.updateNativeSpecRow('[spec-variant-sku]', variant ? variant.sku || '' : '');

        lazyLoadInstance?.update();

        // A swap just re-applies the frozen center captured from the initial
        // card on open — it never re-measures, so the control can't move.
        this.applySwitcherPosition();
      }

      /**
       * Pin the switcher to the INITIAL (page) product card's vertical center and
       * keep that exact pixel for every sibling, so it never jumps when a
       * sibling's spec column wraps to a different height. Measured while the page
       * product is showing (modal open) and re-measured on viewport resize (the
       * only time the initial card's height legitimately changes). The footer
       * keeps its CSS `translateY(-50%)`, so the stored px is its vertical center.
       */
      lockSwitcherPosition() {
        if (!this.siblingSwitchButton) return;
        const content = this.querySelector('.product-specs-modal-content');
        if (!content) return;
        const height = content.getBoundingClientRect().height;
        // Modal still hidden (display:none) — nothing laid out to measure yet.
        if (height === 0) return;
        this.lockedSwitcherTop = height / 2;
        this.applySwitcherPosition();
      }

      /**
       * Apply the frozen center to the footer. No-op until `lockSwitcherPosition`
       * has captured the initial card's center.
       */
      applySwitcherPosition() {
        if (this.lockedSwitcherTop == null || !this.siblingSwitchButton) return;
        const footer = this.siblingSwitchButton.closest('.product-specs-modal-footer');
        if (footer) footer.style.top = `${Math.round(this.lockedSwitcherTop)}px`;
      }

      /**
       * Replace a spec group's rows with server-rendered HTML. Same contract as
       * `_DefaultProductMetaHandler._setSpecGroupHtml`: blank markup leaves a
       * truly empty container so the `:empty { display: none }` rule collapses
       * it; non-blank markup is DOMPurify-sanitized when available.
       *
       * @param {HTMLElement|null} container - The `[data-spec-group]` container
       * @param {string} html - Server-rendered rows HTML
       */
      setSpecGroupHtml(container, html) {
        if (!container || typeof html !== 'string') return;

        const trimmedHtml = html.trim();
        if (trimmedHtml === '') {
          container.innerHTML = '';
          return;
        }

        container.innerHTML = window.DOMPurify ? window.DOMPurify.sanitize(trimmedHtml) : trimmedHtml;
      }

      /**
       * Update a native row's value and hide the whole row when the displayed
       * product has no value for it (mirrors the server-side row gating).
       *
       * @param {string} valueSelector
       * @param {string} value
       */
      updateNativeSpecRow(valueSelector, value) {
        const valueElement = this.querySelector(valueSelector);
        if (!valueElement) return;
        valueElement.textContent = value;
        const rowElement = valueElement.closest('[data-spec]');
        if (rowElement) rowElement.hidden = value === '';
      }

      /**
       * Swap the dimension image to the displayed product's URL, mirroring
       * `_DefaultProductMetaHandler._updateDimensionImage`. When the product has
       * no dimension image, hide the image container (and its divider) and
       * restore both when cycling back to a product that has one.
       *
       * Also re-points the accessible text at the displayed sibling so AT no
       * longer announces the previous product's dimensions after a swap: a short
       * `<img alt>` ("<model> dimensions") plus the full localized sentence from
       * the sibling's `dimension_detail` JSON state in the adjacent `sr-only`
       * block (kept in lockstep by `product-dimension-detail.liquid`).
       *
       * @param {Object} productState - Parsed sibling JSON state (reads
       *   `dimension_image`, `dimension_detail`, and the display-name fields)
       */
      updateDimensionImage(productState) {
        const pictureElement = this.querySelector('[data-product-dimension-image]');
        if (!pictureElement) return;

        const dimensionImageUrl = productState ? productState.dimension_image : '';
        const imageContainer = pictureElement.closest('.product-specs-modal-image');
        let imageDivider = null;
        if (imageContainer && imageContainer.nextElementSibling?.tagName === 'HR') {
          imageDivider = imageContainer.nextElementSibling;
        }

        const hasImage = typeof dimensionImageUrl === 'string' && dimensionImageUrl.trim() !== '';
        if (imageContainer) imageContainer.hidden = !hasImage;
        if (imageDivider) imageDivider.hidden = !hasImage;
        if (!hasImage) return;

        pictureElement.querySelectorAll('source').forEach((sourceElement) => {
          sourceElement.setAttribute('data-srcset', dimensionImageUrl);
          sourceElement.setAttribute('srcset', dimensionImageUrl);
        });

        const imageElement = pictureElement.querySelector('img');
        if (imageElement) {
          imageElement.setAttribute('data-src', dimensionImageUrl);
          imageElement.setAttribute('src', dimensionImageUrl);
          // Prefer the localized alt emitted in the sibling's JSON state; fall back
          // to the English template only when a sibling lacks the field.
          const localizedAlt =
            productState && typeof productState.dimension_alt === 'string'
              ? productState.dimension_alt.trim()
              : '';
          imageElement.alt = localizedAlt || `${this.getProductStateDisplayName(productState)} dimensions`;
        }

        // Re-point the sr-only detail at the displayed sibling (or clear it when
        // the sibling has no dimension sentence) so the full reading matches.
        const detailElement = imageContainer
          ? imageContainer.querySelector('[data-specs-dimension-detail]')
          : null;
        if (detailElement) {
          const dimensionDetail =
            productState && typeof productState.dimension_detail === 'string'
              ? productState.dimension_detail.trim()
              : '';
          detailElement.textContent = dimensionDetail;
        }
      }

      /**
       * Refresh the switcher's visible name + aria-label to name the NEXT card
       * in the cycle relative to the currently displayed one.
       */
      updateSiblingSwitchButton() {
        if (!this.siblingSwitchButton) return;

        const nextProductState = this.getNextProductState(this.displayedProductId);
        if (!nextProductState) return;

        const nextModelName = this.getProductStateDisplayName(nextProductState);

        // Icon-only button — the next model name lives only in the aria-label.
        const labelTemplate = this.siblingSwitchButton.getAttribute('data-switch-label-template') || '';
        if (labelTemplate) {
          this.siblingSwitchButton.setAttribute('aria-label', labelTemplate.replace('{model}', nextModelName));
        }
      }

      /**
       * @param {Object} productState
       * @returns {string} Model name with native-title fallback
       */
      getProductStateDisplayName(productState) {
        return (productState.model_name || productState.title_native || '').trim();
      }

      /**
       * Politely announce the card change via the `role="status"` live region;
       * the text is cleared again after ~3s.
       *
       * @param {string} modelName
       */
      announceCardChange(modelName) {
        if (!this.siblingAnnouncementElement || !this.siblingSwitchButton) return;
        const announcementTemplate =
          this.siblingSwitchButton.getAttribute('data-switch-announcement-template') || '';
        if (!announcementTemplate) return;

        this.clearAnnouncementTimeout();
        this.siblingAnnouncementElement.textContent = announcementTemplate.replace('{model}', modelName);
        this.announcementTimeout = setTimeout(() => {
          this.siblingAnnouncementElement.textContent = '';
          this.announcementTimeout = null;
        }, this.announcementDurationMilliseconds);
      }

      clearAnnouncementTimeout() {
        if (this.announcementTimeout) {
          clearTimeout(this.announcementTimeout);
          this.announcementTimeout = null;
        }
      }

      /**
       * Slide between cards like a true carousel (the product gallery): the
       * outgoing and incoming cards are BOTH on screen and translate together, so
       * the panel never shows a blank gap mid-transition.
       *
       * Mechanism: clone the current card into a non-interactive "ghost" that
       * holds the OLD content, render the NEW content into the real
       * `[data-specs-view]`, stack both absolutely inside the panel (which already
       * clips with `overflow: hidden`), then animate them as a pair — the ghost
       * exits one edge while the real view enters from the opposite edge. The
       * ghost is removed when the slide settles. `direction` picks the edge:
       * forward exits left / enters from the right; backward is the mirror.
       *
       * Honors prefers-reduced-motion with an instant swap. The switcher arrow and
       * the pagination dots sit OUTSIDE the panel, so focus and the position
       * indicator stay put throughout. Re-entrant switches are ignored while a
       * slide is in flight (avoids overlapping ghosts) — a 300ms window.
       *
       * @param {Function} applyUpdate - Writes the new card content
       * @param {'forward'|'backward'} [direction='forward']
       */
      slideCardUpdate(applyUpdate, direction = 'forward') {
        const view = this.specsView;
        const panel = view.closest('.product-specs-modal-panel');

        // Reduced motion (or no panel to clip against) → instant swap.
        if (this.prefersReducedMotion || !panel) {
          applyUpdate();
          return;
        }

        // One slide at a time: drop a switch that arrives mid-animation so we
        // never stack two ghosts. The dropped step is a no-op (state untouched).
        if (this.isSliding) return;
        this.isSliding = true;

        const isForward = direction !== 'backward';
        const incomingStart = isForward ? '100%' : '-100%';
        const outgoingEnd = isForward ? '-100%' : '100%';

        // Snapshot the OLD card as a ghost (clone the current, pre-update view):
        // it holds the outgoing content. Stripped of ids (no duplicates) and
        // hidden from assistive tech / interaction — purely the exit visual. NOT
        // inserted into the DOM yet: that has to wait until after `applyUpdate`,
        // so renderSpecCard's `this.querySelector(...)` hooks resolve to the real
        // view and not the clone (which would otherwise shadow them in DOM order
        // and swap the cards).
        const ghost = view.cloneNode(true);
        ghost.classList.add('product-specs-view-ghost');
        ghost.removeAttribute('data-specs-view');
        ghost.setAttribute('aria-hidden', 'true');
        ghost.inert = true;
        ghost.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        // Measure the outgoing size while the view is still in normal flow, then
        // pin BOTH the panel width and (below) its height for the slide. On desktop
        // `.modal-slide-up__content` is `width: max-content`, so once the cards go
        // absolute (out of flow) the panel would otherwise collapse to zero width
        // and the slide would have nothing to travel across — the new card never
        // appears to move. (Mobile keeps a full-width panel, which is why it
        // worked there but not on desktop.) Pin the width before adding
        // `is-sliding` so the view renders/measures at the correct width.
        const outgoingHeight = view.offsetHeight;
        // Fractional width (not rounded offsetWidth) so pinning can't shave a
        // sub-pixel and reflow/re-wrap the spec text.
        panel.style.width = `${panel.getBoundingClientRect().width}px`;

        // Make the view the absolute incoming card, move it off the incoming edge,
        // and render the new content into it while it is off-screen (ghost not yet
        // in the DOM, so the hooks resolve to this real view).
        panel.classList.add('is-sliding');
        view.style.transition = 'none';
        view.style.transform = `translateX(${incomingStart})`;
        applyUpdate();

        // Hold the panel to the taller card so neither clips, then drop the ghost
        // (old content) in at rest so old + new are both on screen.
        const incomingHeight = view.offsetHeight;
        panel.style.height = `${Math.max(outgoingHeight, incomingHeight)}px`;
        panel.insertBefore(ghost, view);
        ghost.style.transition = 'none';
        ghost.style.transform = 'translateX(0)';

        // Commit the start positions, then release to the CSS transition and animate
        // both cards as a pair on the next frame.
        void panel.offsetWidth;
        ghost.style.transition = '';
        view.style.transition = '';
        requestAnimationFrame(() => {
          ghost.style.transform = `translateX(${outgoingEnd})`;
          view.style.transform = 'translateX(0)';
        });

        let settled = false;
        const finish = (event) => {
          // Ignore transitionend from descendants or non-transform properties.
          if (event && (event.target !== view || event.propertyName !== 'transform')) return;
          if (settled) return;
          settled = true;
          view.removeEventListener('transitionend', finish);
          this.clearSlideState();
        };

        // Track the listener + fallback timer on the instance so disconnect (or a
        // reopen) can tear them down — otherwise a slide left in flight when the
        // element detaches fires finish → clearSlideState on a detached node.
        this.handleSlideTransitionEndBound = finish;
        view.addEventListener('transitionend', finish);
        // Fallback in case transitionend does not fire.
        this.slideFallbackTimeout = setTimeout(finish, this.slideDurationMilliseconds + 50);
      }

      /**
       * Tear down any in-flight slide: remove the ghost, drop the panel's fixed
       * height and `is-sliding` flag, and clear the real view's inline transform /
       * transition so it returns to normal flow. Safe to call when no slide is
       * active. Also used on reopen so a modal closed mid-slide can't reopen with
       * the card left translated off the panel edge.
       */
      clearSlideState() {
        this.isSliding = false;

        // Tear down the in-flight slide's fallback timer and transitionend
        // listener. Idempotent: clearTimeout(null) is a no-op and
        // removeEventListener for an already-removed (or never-added) listener is
        // harmless, so this stays safe when finish already ran or no slide was active.
        clearTimeout(this.slideFallbackTimeout);
        this.slideFallbackTimeout = null;
        if (this.specsView && this.handleSlideTransitionEndBound) {
          this.specsView.removeEventListener('transitionend', this.handleSlideTransitionEndBound);
        }

        if (!this.specsView) return;
        const panel = this.specsView.closest('.product-specs-modal-panel');
        if (panel) {
          panel.classList.remove('is-sliding');
          panel.style.width = '';
          panel.style.height = '';
          panel.querySelectorAll('.product-specs-view-ghost').forEach((ghost) => ghost.remove());
        }
        this.specsView.style.transition = '';
        this.specsView.style.transform = '';
      }

      /**
       * The page's current product + variant. `[data-selected-variant]` is kept
       * current by `_DefaultProductMetaHandler` on every page-side change, so
       * looking its variant id up across the sibling states identifies the page
       * card even after client-side sibling switches. Falls back to the
       * server-rendered initial product/variant ids on the button.
       *
       * @returns {{productState: Object, variant: Object|null}|null}
       */
      resolvePageState() {
        let pageVariantId = null;
        const selectedVariantScript = this.productScope.querySelector('script[data-selected-variant]');
        if (selectedVariantScript) {
          try {
            const selectedVariant = JSON.parse(selectedVariantScript.textContent);
            if (selectedVariant && selectedVariant.id != null) pageVariantId = String(selectedVariant.id);
          } catch (error) {
            console.warn('product-specs-modal: unparseable selected-variant JSON', error);
          }
        }

        if (pageVariantId) {
          for (const productState of this.siblingProductStates) {
            const variants = Array.isArray(productState.variants) ? productState.variants : [];
            const pageVariant = variants.find((variant) => String(variant.id) === pageVariantId);
            if (pageVariant) return { productState, variant: pageVariant };
          }
        }

        const initialProductId = this.siblingSwitchButton.getAttribute('data-initial-product-id');
        const initialVariantId = this.siblingSwitchButton.getAttribute('data-initial-variant-id');
        const productState =
          this.siblingProductStates.find((candidate) => String(candidate.id) === String(initialProductId)) ||
          this.siblingProductStates[0];
        if (!productState) return null;

        const variants = Array.isArray(productState.variants) ? productState.variants : [];
        const initialVariant = variants.find((variant) => String(variant.id) === String(initialVariantId));
        const firstAvailableVariant = variants.find((variant) => variant.available === 'true');
        return { productState, variant: initialVariant || firstAvailableVariant || variants[0] || null };
      }

      /**
       * Reopening always shows the page's current product + variant. A re-render
       * is only needed when the switcher previously left a different card in the
       * DOM — otherwise the server-rendered / MetaHandler-managed card is already
       * correct and is left untouched.
       */
      resetToPageProductCard() {
        if (!this.siblingSwitchButton || !this.siblingProductStates || this.siblingProductStates.length < 2) {
          return;
        }

        this.clearAnnouncementTimeout();
        if (this.siblingAnnouncementElement) this.siblingAnnouncementElement.textContent = '';

        // Tear down any in-flight slide (e.g. the modal was closed mid-slide) so a
        // reopen always shows a settled, on-screen card.
        this.clearSlideState();

        const pageState = this.resolvePageState();
        if (!pageState) return;

        if (this.cardSwapped) {
          this.renderSpecCard(pageState.productState, pageState.variant);
          this.cardSwapped = false;
        }

        this.displayedProductId = String(pageState.productState.id);
        this.updateSiblingSwitchButton();
        this.updateSiblingDots();
      }

      show(opener) {
        this.opener = opener;
        this.resetToPageProductCard();
        window.pauseAllMedia();
        this.style.display = 'block';
        this.classList.add('modal-show');

        lazyLoadInstance?.update();

        setTimeout(() => {
          document.body.style.overflow = 'hidden';
          this.classList.add('modal-visuallyshow');
          // Re-measure once visible in case layout settled (e.g. fonts) after open.
          this.lockSwitcherPosition();
        }, 300);

        // Add focus trapping after the modal is shown, and capture the initial
        // card's center now that it is laid out (display:block).
        requestAnimationFrame(() => {
          trapFocus(this);
          this.lockSwitcherPosition();
        });
      }
    }
  );
}
